import React, { useState } from "react";
import { Heart, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/PageHero";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/lib/assets";

const PRESETS = [25, 50, 100, 250];
const IMPACT = {
  25: "Helps support financial literacy materials for families",
  50: "Helps support youth mentorship programming",
  100: "Helps support homeownership readiness education",
  250: "Helps support the Foundation's community programs and operations",
};

export default function Donate() {
  const [frequency, setFrequency] = useState("one-time");
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const effective = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    if (!effective || effective <= 0) { toast.error("Please enter a valid donation amount."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/donate", {
        amount: effective, frequency, donor_name: name, donor_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <main>
      <PageHero overline="Support Our Mission" image={IMG.youth1}
        title="Your gift opens doors"
        subtitle="Every donation funds mentorship, housing readiness, financial education, and direct assistance for families across our community." />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 bg-white rounded-3xl border border-slate-200 p-8 shadow-xl">
          <h2 className="font-display text-2xl font-bold text-navy-900 mb-6">Make a donation</h2>

          <div className="inline-flex p-1 bg-slate-100 rounded-full mb-7" data-testid="donation-frequency-toggle">
            {["one-time", "monthly"].map((f) => (
              <button key={f} onClick={() => setFrequency(f)}
                data-testid={`donation-frequency-${f}`}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold capitalize transition-colors ${frequency === f ? "bg-navy-900 text-white" : "text-slate-600"}`}>
                {f === "one-time" ? "One-Time" : "Monthly"}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {PRESETS.map((p) => (
              <button key={p} onClick={() => { setAmount(p); setCustom(""); }}
                data-testid="donation-amount-preset"
                className={`py-4 rounded-xl font-display text-xl font-bold border-2 transition-colors ${!custom && amount === p ? "border-gold bg-gold/10 text-navy-900" : "border-slate-200 text-navy-800 hover:border-navy-700"}`}>
                ${p}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <Label className="text-sm text-slate-600">Or enter a custom amount</Label>
            <div className="relative mt-2">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-navy-900 font-bold">$</span>
              <Input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)}
                data-testid="donation-custom-amount" placeholder="Custom amount" className="pl-8 h-12 rounded-xl" />
            </div>
          </div>

          {IMPACT[effective] && (
            <p className="flex items-center gap-2 text-sm text-verified bg-verified/5 rounded-lg p-3 mb-6">
              <CheckCircle2 className="h-4 w-4" /> {IMPACT[effective]}
            </p>
          )}

          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div><Label className="text-sm text-slate-600">Name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12 rounded-xl" data-testid="donor-name" /></div>
            <div><Label className="text-sm text-slate-600">Email (optional)</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 rounded-xl" data-testid="donor-email" /></div>
          </div>

          <Button onClick={donate} disabled={loading} data-testid="donation-submit-btn"
            className="w-full h-14 rounded-full bg-gold hover:bg-gold-light text-white font-bold text-base shadow-lg shadow-gold/20">
            {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Redirecting to secure checkout...</> :
              <><Heart className="h-5 w-5 mr-2" /> Donate ${effective || 0} {frequency === "monthly" ? "/ month" : ""}</>}
          </Button>
          <p className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-4">
            <ShieldCheck className="h-4 w-4 text-verified" /> Secure payment processed by Stripe
          </p>
        </div>

        <div className="lg:col-span-2">
          <img src={IMG.youth2} alt="Youth in community program" className="rounded-3xl w-full h-56 object-cover mb-6 shadow-lg" />
          <div className="bg-navy-900 rounded-3xl p-7 text-white">
            <h3 className="font-display text-xl font-bold mb-3">Where your gift goes</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              As a registered 501(c)(3), we direct your support to Foundation-operated programs — mentorship, micro-grants,
              and community workshops. We receive zero commercial kickbacks or affiliate fees.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
